import { ErrorHandler, Injectable } from "@angular/core";
import { LoggerService } from '../logger/logger.service';

import * as Sentry from '@sentry/angular';

// global error handler catch chuncks loading issues and log them. other errors should be treated in their origin file 
@Injectable({
    providedIn: "root"
})
export class GlobalErrorHandler implements ErrorHandler {
	private readonly loggerName = 'GlobalErrorHandler';
	// matches stale-chunk failures across bundlers/browsers. The dynamic-import
	// variants are anchored to the esbuild/Vite chunk URL (chunk-<hash>.js) so a
	// third-party lib that lazy-loads its own sub-modules (Monaco, Chart.js, …)
	// and throws a similar message on a genuine network error won't trigger a reload:
	// - Webpack: "Loading chunk 123 failed"
	// - esbuild/Vite (Chrome): "Failed to fetch dynamically imported module: .../chunk-XXXX.js"
	// - Firefox: "error loading dynamically imported module: .../chunk-XXXX.js"
	// - Safari: "Importing a module script failed" (no URL in the message)
	private readonly chunkFailedMessage = /Loading chunk [\d]+ failed|(?:Failed to fetch|error loading) dynamically imported module.*chunk-[A-Za-z0-9]+\.js|Importing a module script failed/i;
	private readonly reloadGuardKeyPrefix = 'chunk-reload-attempt';
	// if a reload was triggered within this window and the chunk still fails,
	// stop retrying — the problem is not a stale deploy and we would loop forever.
	// The guard is keyed per-route so a second stale lazy route after the same
	// deploy can still auto-recover instead of being treated as a loop.
	private readonly reloadGuardWindowMs = 10_000;

	constructor(
		private logger: LoggerService
	) { }

	private getReloadGuardKey(): string {
		return `${this.reloadGuardKeyPrefix}:${window.location.pathname}`;
	}

	handleError(error: any): void {
    	if(error?.message && this.chunkFailedMessage.test(error.message)) {
			const guardKey = this.getReloadGuardKey();
			let lastAttempt = 0;
			try {
				lastAttempt = Number(sessionStorage.getItem(guardKey)) || 0;
			} catch { /* sessionStorage unavailable (private mode / sandboxed iframe) — treat as first attempt */ }

			if (Date.now() - lastAttempt < this.reloadGuardWindowMs) {
				this.logger.info(this.loggerName, 'Chunk load failed again after reload, not retrying');
			} else {
				try {
					sessionStorage.setItem(guardKey, String(Date.now()));
				} catch { /* ignore — reload will still fire, guard just won't persist */ }
				this.logger.info(this.loggerName, 'Chunk load failed, refreshing to new version');
				window.location.reload();
				return;
			}
		}

		// Log to console
		console.error(error);

		Sentry.withScope(scope => {
			scope.setLevel('error');
			scope.setTag('error_type', 'unhandled_error');
			scope.setExtra('error_message', error?.message);
			scope.setExtra('stack_trace', error?.stack);
			Sentry.captureException(error);
		});
	}
}
